import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { ProtocolType, ProtocolData } from 'src/lib/fliko/models/agreements/protocol.data';
import { TenancyAgreementData } from 'src/lib/fliko/models/agreements/tenancyAgr.data';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { NoteType } from 'src/lib/fliko/models/notes/notes.data';
import { ApartmentData } from 'src/lib/fliko/models/units/apartment.data';
import { ParkingData } from 'src/lib/fliko/models/units/parking.data';
import { StorageData } from 'src/lib/fliko/models/units/storage.data';
import { RentalUnitType } from 'src/lib/fliko/models/units/unit.data';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { GrammarHelper } from 'src/app/shared/helpers/grammar.helper';
import { LocalizationHelper } from 'src/app/shared/helpers/localization.helper';
import { DeleteFileService } from 'src/app/shared/services/delete-file.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { ResidentsListBuilder } from '../../../helpers/resident-list-builder';
import { FileCategory } from '../../../services/models/attachments-model';
import { UserPRSList } from '../../../services/models/users-table.data';
import { UnitResidentViewModel } from '../../../services/models/users-view-models';
import { ProtocolService } from '../../../services/protocol.service';
import { UsersDocumentService } from '../../../services/users-document.service';

@Component({
  selector: 'app-users-prs-rental-unit-details',
  templateUrl: './users-prs-rental-unit-details.component.html',
  styleUrls: ['./users-prs-rental-unit-details.component.scss']
})
export class UsersPrsRentalUnitDetailsComponent implements OnInit, OnDestroy {
  public readonly PROTOCOL_TYPES = [ProtocolType.ACCEPTANCE, ProtocolType.BEFORE_ACCEPTANCE, ProtocolType.DELIVERY];

  public protocols: Map<ProtocolType, ProtocolData> = new Map();
  public residents: UnitResidentViewModel[];

  public apartment?: ApartmentData;
  public parking?: ParkingData;
  public storage?: StorageData;

  public canEdit = false;
  public showUnitCodes = false;

  public protType = (x: ProtocolType | undefined) => GrammarHelper.protocolName(x, this.ls);
  public get unitType() {
    return GrammarHelper.unitTypeHR(this.data.type, EstateType.PRS, this.ls);
  }

  private readonly locHelper = new LocalizationHelper(this.ls);

  private subscription$: Subscription = new Subscription();

  NoteType = NoteType;
  ProtocolType = ProtocolType;
  EstateType = EstateType;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UserPRSList.RentalUnitElement,
    private dialogRef: MatDialogRef<UsersPrsRentalUnitDetailsComponent>,
    private protocolService: ProtocolService,
    private usersDocuments: UsersDocumentService,
    private router: Router,
    private dialogService: DialogService,
    private deleteFile: DeleteFileService,
    private ls: LocalizationService,
    private ctxDS: ContextDataService,
    private errorHandlingService: ErrorHandlingService
  ) {
    this.residents = ResidentsListBuilder.forPRS(data);
    switch (data.type) {
      case RentalUnitType.APARTMENT:
        this.apartment = data.unit as ApartmentData;
        break;
      case RentalUnitType.PARKING:
        this.parking = data.unit as ParkingData;
        break;
      case RentalUnitType.STORAGE:
        this.storage = data.unit as StorageData;
        break;
    }
  }

  ngOnInit(): void {
    this.getProtocols();

    this.subscription$.add(
      this.ctxDS.contextPerms$.subscribe((perms) => (this.canEdit = perms?.superAdmin || !!perms?.ePerm?.user?.edit))
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  onEdit(): void {
    this.router.navigate(['/users', this.data.type + 's', 'edit', this.data.unit.id]);
    this.dialogRef.close();
  }

  public onArchivedAgreement(agreement: TenancyAgreementData): void {
    this.router.navigate(['/users/agreements', agreement.id]);
    this.dialogRef.close();
  }

  public onAddProtocol(type: ProtocolType): void {
    this.router.navigate([
      '/users',
      'agreements',
      this.data.agreement?.id,
      'protocols',
      type,
      'create',
      this.data.type,
      this.data.unit.id
    ]);
    this.dialogRef.close();
  }

  public onEditProtocol(type: ProtocolType, id: string): void {
    this.router.navigate([
      '/users',
      'agreements',
      this.data.agreement?.id,
      'protocols',
      type,
      'edit',
      id,
      this.data.type,
      this.data.unit.id
    ]);
    this.dialogRef.close();
  }

  public onRemoveProtocol(protocol: ProtocolData): void {
    const $date = this.locHelper.localizeDate(protocol.timestamp, 'dLong');
    const $name = GrammarHelper.protocolName(protocol.type, this.ls);
    this.subscription$.add(
      this.dialogService
        .openConfirmationDialog({
          text: this.ls.trReplace('guard.protDel', { $name, $date }),
          acceptKey: 'button.delete',
          cancelKey: 'button.cancel'
        })
        .subscribe(async (result) => {
          if (!result || this.ctxDS.peekCurrentEstateId() !== protocol.estateId) return;
          try {
            await this.usersDocuments.updateUsersDocs(
              protocol.attachments,
              [],
              protocol.attachments,
              protocol.landlordIds,
              [],
              protocol.estateId,
              protocol.agreementId,
              protocol.id,
              FileCategory.PROTOCOL
            );
            if (this.ctxDS.peekCurrentEstateId() !== protocol.estateId) return;
            protocol.attachments.forEach((attachment) => this.deleteFile.addFileToDelete(attachment));
            await Promise.all([
              this.deleteFile.deleteFiles(),
              this.protocolService.updateProtocol({ ...protocol, deleted: true })
            ]);
            this.dialogRef.close();
          } catch (error) {
            this.errorHandlingService.showGlobalError('error.request', error);
          }
        })
    );
  }

  private getProtocols(): void {
    if (this.data.agreement) {
      this.subscription$.add(
        this.protocolService
          .getAgreementProtocols$(this.data.agreement.id)
          .pipe(take(1))
          .subscribe((protocols) => {
            protocols.filter((x) => x.rentalUnitId === this.data.unit.id).forEach((x) => this.protocols.set(x.type, x));
          })
      );
    }
  }

  public get number(): string {
    return ToString.longNumber(this.data.unit);
  }
}
